import React from 'react';
import {
  LegalPageLayout,
  LegalPageTitle,
  LegalPageUpdated,
  LegalNotice,
  LegalSection,
  LegalHeading,
  LegalBody,
  LegalList,
} from '../../shared/layouts/LegalPageLayout';

export interface TermsOfServicePageProps {
  onBack: () => void;
}

export const TermsOfServicePage: React.FC<TermsOfServicePageProps> = ({ onBack }) => {
  return (
    <LegalPageLayout onBack={onBack}>
      <LegalPageTitle>Terms of Service</LegalPageTitle>
      <LegalPageUpdated>Last updated: {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}</LegalPageUpdated>

      <LegalNotice>
        This is a working draft written to match how Tvikla OS actually operates. It has not been
        reviewed by a lawyer and should not be published or relied on as a final contract until
        qualified counsel has reviewed it, particularly the liability, payments, and export-compliance
        sections.
      </LegalNotice>

      <LegalSection>
        <LegalHeading>1. Who these terms cover</LegalHeading>
        <LegalBody>
          These terms apply to exporters, buyers, and any team members they invite who use Tvikla OS to
          create, track, or settle a trade order. By using the platform you agree to them.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>2. What Tvikla is — and isn&apos;t</LegalHeading>
        <LegalBody>
          Tvikla OS is a platform that connects production, marketplace, logistics, insurance, customs,
          compliance, payments, documents, and messaging into one workflow, and integrates with the
          carrier, banking, and government systems (such as ICEGATE, ULIP, and DGFT) an export actually
          runs on. Tvikla is not a customs broker, freight forwarder, bank, or insurer — it connects you
          to those licensed parties and does not replace the legal responsibilities they, or you, hold
          as the exporter or buyer of record.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>3. Accounts</LegalHeading>
        <LegalList>
          <li>You must provide accurate business and trade credentials (IEC, GST, AD code, etc.) and keep them current.</li>
          <li>You are responsible for activity under your account, including team members you invite.</li>
          <li>We may suspend an account that provides false credentials, violates these terms, or is used for fraud.</li>
        </LegalList>
      </LegalSection>

      <LegalSection>
        <LegalHeading>4. Marketplace conduct</LegalHeading>
        <LegalBody>
          Exporters listed on the marketplace must be who they claim to be and fulfil orders as
          described. Buyers are expected to pay for confirmed orders in good faith. Tvikla can remove
          listings or accounts that misrepresent goods, pricing, or trade credentials.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>5. Payments &amp; fees</LegalHeading>
        <LegalBody>
          Payments, collections, and reconciliation are processed through our banking and payment
          partners, not held by Tvikla directly. Subscription fees for exporter plans are billed as
          described on our pricing page; payment processing fees charged by our banking partners are
          separate and disclosed before you confirm a transaction.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>6. Compliance responsibility</LegalHeading>
        <LegalBody>
          Tvikla helps you file and track customs and regulatory filings, but the exporter or buyer of
          record remains legally responsible for the accuracy of the goods, valuation, and compliance
          information submitted through the platform. Tvikla is not liable for penalties arising from
          incorrect information you provide.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>7. Liability</LegalHeading>
        <LegalBody>
          Tvikla is a coordination layer across third-party carriers, insurers, banks, and government
          systems. To the extent permitted by law, our liability is limited to fees you paid Tvikla in
          the twelve months before a claim, and does not cover losses caused by those third parties,
          which are governed by their own terms (for example, a carrier&apos;s liability for cargo, or
          an insurer&apos;s policy terms).
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>8. Termination</LegalHeading>
        <LegalBody>
          You may close your account at any time. We may suspend or terminate access for breach of
          these terms, non-payment, or fraud. Orders already in progress at termination are handled
          under the terms that applied when they were created.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>9. Changes to these terms</LegalHeading>
        <LegalBody>
          We&apos;ll notify you through the platform before material changes take effect. Continued use
          after that point means you accept the updated terms.
        </LegalBody>
      </LegalSection>

      <LegalSection>
        <LegalHeading>10. Contact</LegalHeading>
        <LegalBody>Questions about these terms can be sent through the Help Center.</LegalBody>
      </LegalSection>
    </LegalPageLayout>
  );
};

export default TermsOfServicePage;
